import React from 'react'
import * as R from 'ramda'
import { t } from 'ttag'

import { CompetenciesFormFields } from 'components/shared/CompetenciesFormFields/CompetenciesFormFields'
import { CompetencyDescription } from 'components/shared/CompetencyDescription/CompetencyDescription'
import { DetailsCard } from 'components/shared/DetailsCard/DetailsCard'
import { Form } from 'components/shared/Form/Form'
import { DefaultDescription } from './common/DefaultDescription/DefaultDescription'
import { fetchDepartments } from 'api/departments'
import {
  getCategoryOrder,
  getCompetencies,
  getIsCompetenciesDisabled,
} from 'components/shared/CompetenciesFormFields/helpers'
import { useActiveCategory } from 'hooks'
import { NON_FIELD_ERRORS, getApiError } from 'utils/apiErrors'
import { getLanguages } from 'utils/languages'
import {
  TEMPLATE_LANGUAGE_FIELD,
  TEMPLATE_NAME_FIELD,
  DEPARTMENT_FIELD,
} from './model'
import { TEMPLATE_NAME_LIMIT } from './schema'

import styles from './CreateInterviewTemplateForm.module.scss'

export const CreateInterviewTemplateForm = ({
  competencies: { fields },
  control,
  formState,
  language,
  watch,
}) => {
  const categories = getCompetencies(fields, language)
  const categoryOrder = getCategoryOrder(language)
  const isCompetenciesDisabled = false // getIsCompetenciesDisabled(formState)

  const {
    output: activeCategory,
    handleOutputChange,
    handleReset,
    hasOutput: hasActiveCategory,
  } = useActiveCategory()

  return (
    <Form className={styles.form}>
      <div className={styles.description}>
        <Form.Field
          className={styles.field}
          count={watch(TEMPLATE_NAME_FIELD)?.length}
          error={
            getApiError(formState.errors?.root?.apiError, NON_FIELD_ERRORS) ??
            formState.errors[TEMPLATE_NAME_FIELD]?.message
          }
          field="input"
          hint={t`Name needs to be unique.`}
          label={t`Interview template name`}
          limit={TEMPLATE_NAME_LIMIT}
          name={TEMPLATE_NAME_FIELD}
          {...{ control }}
        />
        <Form.Field
          className={styles.field}
          error={formState.errors[DEPARTMENT_FIELD]?.message}
          field="combobox"
          hint={t`Departments group jobs together and allow easy access management`}
          label={t`Department`}
          name={DEPARTMENT_FIELD}
          fetchOptions={[
            fetchDepartments,
            ({ name, id }) => ({ label: name, value: id }),
          ]}
          isRequired
          isSearchable
          {...{ control }}
        />
        <Form.Field
          className={styles.field}
          error={formState.errors[TEMPLATE_LANGUAGE_FIELD]?.message}
          field="combobox"
          hint={t`Select a language for the competency-based questions and the interview module.`}
          label={t`Language`}
          name={TEMPLATE_LANGUAGE_FIELD}
          options={getLanguages()}
          {...{ control }}
        />
      </div>
      <h3 className={styles.subtitle}>{t`Choose competencies`}</h3>
      <div className={styles.skills}>
        <CompetenciesFormFields
          className={styles.competencies}
          order={categoryOrder}
          onClick={handleOutputChange}
          {...{
            activeCategory,
            isCompetenciesDisabled,
            categories,
            control,
            formState,
          }}
        />
        <aside className={styles.aside}>
          <DetailsCard
            title={hasActiveCategory ? activeCategory : t`About competencies`}
            onBackClick={handleReset}
            hasDefaultState={R.not(hasActiveCategory)}
          >
            {hasActiveCategory ? (
              <CompetencyDescription
                competencies={
                  R.reduceBy(
                    (acc, { description, label, name }, index) => [
                      ...acc,
                      { description, index, label, name },
                    ],
                    [],
                    ({ category }) => category,
                    categories,
                  )[activeCategory]
                }
              />
            ) : (
              <DefaultDescription />
            )}
          </DetailsCard>
        </aside>
      </div>
    </Form>
  )
}
